import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';

/** Set while a page with a live feed (Monitoring) is open; the workspace chrome shows its "Live" state. */
export interface WorkspaceLiveState {
  connected: boolean;
}

// Value and setter live in separate contexts so the page that reports its
// state never re-renders because of it.
const LiveStateContext = createContext<WorkspaceLiveState | null>(null);
const SetLiveStateContext = createContext<(state: WorkspaceLiveState | null) => void>(() => undefined);

export function WorkspaceLiveProvider({ children }: { children: React.ReactNode }) {
  const [live, setLive] = useState<WorkspaceLiveState | null>(null);
  return (
    <SetLiveStateContext.Provider value={setLive}>
      <LiveStateContext.Provider value={live}>{children}</LiveStateContext.Provider>
    </SetLiveStateContext.Provider>
  );
}

/** What the header shows: null when the open page has no live feed. */
export function useWorkspaceLiveState(): WorkspaceLiveState | null {
  return useContext(LiveStateContext);
}

/** Called by a live page with its socket state; cleared when the page unmounts. */
export function useWorkspaceLive(connected: boolean): void {
  const setLive = useContext(SetLiveStateContext);
  useEffect(() => {
    setLive({ connected });
    return () => setLive(null);
  }, [connected, setLive]);
}

/** Green dot + "Jonli" once the feed is connected; a grey dot while it (re)connects. */
export function LiveIndicator({ connected, className }: { connected: boolean; className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-medium',
        connected ? 'text-success-700 dark:text-success-400' : 'text-muted-foreground',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn('h-2 w-2 shrink-0 rounded-full', connected ? 'bg-success-600' : 'bg-muted-foreground/60')}
      />
      {connected ? t('monitor_live') : t('connecting')}
    </span>
  );
}
